import { useEffect, useState, useRef } from "react";
import { FaTimes, FaCloudUploadAlt, FaTrash, FaDownload, FaChevronDown } from "react-icons/fa";
import pathconfig from '../../../pathconfig.json';
import axios from "axios";
import JSZip from "jszip"; // package added by praveen, for download all the files in the attachment in single zip..
import Docx from "../../../images/icons/docx.png";
import Doc from "../../../images/icons/doc.png";
import Pdf from "../../../images/icons/pdf.png";
import Jpg from "../../../images/icons/jpg.png";
import Png from "../../../images/icons/png.png";
import Tif from "../../../images/icons/tif.png";
import Tiff from "../../../images/icons/tiff.png";
import Zip from "../../../images/icons/zip.png";
import Xml from "../../../images/icons/xml.png";
import Json from "../../../images/icons/json.png";
import Ini from "../../../images/icons/ini.png";
import Txt from "../../../images/icons/txt.png";
import Xlsx from "../../../images/icons/Xlsx.png";
import Noimage from "../../../images/icons/noimage.png";
import { toastError, toastSuccess } from '../../toaster';
import moment from 'moment';
import NodataFound from "../../nodata";
import packjson from '../../../../package.json';
import { useAppContextProvider } from '../../../contextapi';

// Media and Image attachments must each be one of these file types.
const ALLOWED_MEDIA_EXTENSIONS = ['ttml', 'txt', 'vtt', 'mp4', 'mp3', 'gif'];
const ALLOWED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf', 'tif', 'tiff', 'eps', 'svg'];
const getFileExtension = (filename) => (filename.split('.').pop() || '').toLowerCase();
const isValidMediaFile = (filename) => ALLOWED_MEDIA_EXTENSIONS.includes(getFileExtension(filename));
const isValidImageFile = (filename) => ALLOWED_IMAGE_EXTENSIONS.includes(getFileExtension(filename));

// Display label for an Image tab file, mirroring the Image tab's own modal
// title (getImageTitle in imagelist.js) — e.g. "Fig. 4 (gr4.pdf)",
// "Inline Image (fx1.pdf)" — so the "Select Image to Replace" list matches
// what the Master Copier actually sees when they open that image.
const IMAGE_TYPE_MAP = {
  gr: "Graphical Image",
  ga: "Graphical Abstract Image",
  fx: "Inline Image",
  mmc: "Supplementary videos and gifs",
};
const getImageDisplayTitle = (name) => {
  if (!name) return "";
  const baseName = name.split(".")[0];
  const match = baseName.match(/^([a-zA-Z]+)(\d+)$/);
  const prefix = match?.[1]?.toLowerCase();
  const number = match?.[2];
  if (prefix === "gr") return `Fig. ${number} (${name})`;
  if (prefix === "sc") return `Scheme ${number}`;
  if (prefix === "pl") return `Plate ${number}`;
  const fullName = IMAGE_TYPE_MAP[prefix] || "";
  return fullName ? `${fullName} (${name})` : name;
};

const Attachment_Sidebar = (props) => {
  const [file, setFile] = useState([]);
  const fileInputRef = useRef(null);
  const closeBtnRef = useRef(null);
  const yesBtnRef = useRef(null);
  const firstCategoryRef = useRef(null);
  const [showconfirm, setConfirm] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  
  const [selectedFilesWithComment, setSelectedFilesWithComment] = useState([]);
  const [attachmentComment, setAttachmentComment] = useState([]);
  const [deleteData, setDeleteData] = useState('');
  const [loader, setLoader] = useState(true);
  const [zipping, setZipping] = useState(false);
  const [imageFileList, setImageFileList] = useState([]);
  const [supplementaryFileList, setSupplementaryFileList] = useState([]);
  const [showValidationError, setShowValidationError] = useState(false);
  const [replaceDropdownOpenFile, setReplaceDropdownOpenFile] = useState(null);
  const replaceDropdownRef = useRef(null);
  const [imageTypeDropdownOpenFile, setImageTypeDropdownOpenFile] = useState(null);
  const imageTypeDropdownRef = useRef(null);
  const { userProfile, setSidebarClose, alttextEnabled, fullData } = useAppContextProvider();


  useEffect(() => {
    if (props.fileItems) {
      console.log('File Items with Comments:', props.fileItems);
      setFile(props.fileItems);
      setLoader(false)
    }
  }, [props.fileItems]);

  // Fetch the list of images from the Image tab's folder so the "Replace Image"
  // dropdown can offer the exact existing image names to attach against.
  useEffect(() => {
    if (!fullData?.Content) return;
    const regex = /(?<!.)\\graphicspath\{\{([^}]*)\}\}/;
    const match = fullData.Content.match(regex);
    if (!match) return;
    const folderPath = match[1].replace(/\/$/, "");

    const getImageFileList = async () => {
      try {
        const url = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].get_image_details}`;
        const response = await axios.post(url, { file_path: folderPath });
        setImageFileList(response.data.Files || []);
      } catch (error) {
        console.log(error);
      }
    };
    getImageFileList();
  }, [fullData]);

  // Fetch the list of Supplementary tab files so the "Replace File" dropdown
  // (Media attachments) can offer the exact existing file names to attach against.
  useEffect(() => {
    if (!userProfile?.docid) return;

    const getSupplementaryFileList = async () => {
      try {
        const GetDetailDataString = localStorage.getItem(`${userProfile.docid}_init-getdetails`);
        const GetDetailData = JSON.parse(GetDetailDataString);
        if (!GetDetailData?.Articledetails?.ArtResourcePath) return;

        const basePath = `${GetDetailData.Articledetails.ArtResourcePath}/Supplementary`;
        const url = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].retrievefiles}`;
        const response = await axios.post(url, { InputPath: basePath });

        // Mirror the Supplementary tab: only top-level files, and expose a
        // plain "name" (like imageFileList's shape) for the dropdown to use.
        const filteredFiles = (response.data || [])
          .filter((f) => !f.path.replace(basePath + "/", "").includes("/"))
          .map((f) => ({ name: f.path.split('/').pop() }));

        setSupplementaryFileList(filteredFiles);
      } catch (error) {
        console.log(error);
      }
    };
    getSupplementaryFileList();
  }, [userProfile]);

  // Close the "Select Image/File to Replace" custom dropdown on outside click or Escape
  useEffect(() => {
    if (!replaceDropdownOpenFile) return;
    const handleClickOutside = (e) => {
      if (replaceDropdownRef.current && !replaceDropdownRef.current.contains(e.target)) {
        setReplaceDropdownOpenFile(null);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') setReplaceDropdownOpenFile(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [replaceDropdownOpenFile]);

  // Close the "Image Type" custom dropdown on outside click or Escape
  useEffect(() => {
    if (!imageTypeDropdownOpenFile) return;
    const handleClickOutside = (e) => {
      if (imageTypeDropdownRef.current && !imageTypeDropdownRef.current.contains(e.target)) {
        setImageTypeDropdownOpenFile(null);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') setImageTypeDropdownOpenFile(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [imageTypeDropdownOpenFile]);

  // Auto-focus close button whenever sidebar is opened (from layout)
  useEffect(() => {    
        if (props.leftsidebarvalue === 'attachement') {
                if (closeBtnRef.current) {
                    closeBtnRef.current.focus();
                }
        }
    }, [props.leftsidebarvalue])
    
  useEffect(() => {
    if (showconfirm && yesBtnRef.current) {
      yesBtnRef.current.focus();
    }
  }, [showconfirm]);

    // Close confirmation modal on ESC key
    useEffect(() => {
      if (!showconfirm) return;
      const handleEsc = (e) => {
        if (e.key === 'Escape') {
          setConfirm(false);
        }
      };
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }, [showconfirm]);
  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles || selectedFiles.length === 0) {
      // No files selected, do nothing and ensure modal does not open
      setShowCommentModal(false);
      setSelectedFilesWithComment([]);
      setAttachmentComment([]);
      return;
    }
    const selectedFiles2 = file.filter((a) =>
      selectedFiles.some((sf) => sf.name === a.Filename)
    );
    if (selectedFiles2.length !== 0) {
      toastError('File is already exit.');
      e.target.value = null;
    } else {
      setSelectedFilesWithComment(selectedFiles);
      setShowCommentModal(true);
    }
  }
useEffect(() => {
  if (showCommentModal) {
    setTimeout(() => {
      if (firstCategoryRef.current) {
        firstCategoryRef.current.focus();
      }
    }, 50);
  }
}, [showCommentModal]);



  const handleUploadWithComment = () => {
    // Safety net: block submission if a Media attachment somehow ended up with
    // an unsupported file extension (the category dropdown already prevents this).
    const invalidMediaFile = selectedFilesWithComment.find(file => {
      const fileMetadata = attachmentComment.find(item => item.file === file.name);
      return fileMetadata?.filetype === 'mediaAttachment' && !isValidMediaFile(file.name);
    });
    if (invalidMediaFile) {
      toastError(`"${invalidMediaFile.name}" is not a valid Media file. Allowed formats: ${ALLOWED_MEDIA_EXTENSIONS.map(ext => '.' + ext).join(', ')}`);
      return;
    }
    // Same safety net for Image attachments.
    const invalidImageFile = selectedFilesWithComment.find(file => {
      const fileMetadata = attachmentComment.find(item => item.file === file.name);
      return fileMetadata?.filetype === 'imageAttachment' && !isValidImageFile(file.name);
    });
    if (invalidImageFile) {
      toastError(`"${invalidImageFile.name}" is not a valid Image file. Allowed formats: ${ALLOWED_IMAGE_EXTENSIONS.map(ext => '.' + ext).join(', ')}`);
      return;
    }

    // Check if all selected files have a type and comment
    const missingFields = selectedFilesWithComment.filter(file => {
      const fileMetadata = attachmentComment.find(item => item.file === file.name);
      const isImage = fileMetadata?.filetype === 'imageAttachment';
      const isMedia = fileMetadata?.filetype === 'mediaAttachment';
      const baseInvalid = !fileMetadata || !fileMetadata.filetype || !fileMetadata.comment;
      const altTextInvalid = alttextEnabled && isImage && !fileMetadata?.altTextGeneration;
      // Image Type / File Type (New vs Replace) is only shown when alttextEnabled —
      // no field to fill in, so nothing to require, when it's off.
      const imageActionInvalid = alttextEnabled && isImage && (!fileMetadata?.imageAction || (fileMetadata.imageAction === 'replace' && !fileMetadata.replaceImageName));
      const mediaActionInvalid = alttextEnabled && isMedia && (!fileMetadata?.mediaAction || (fileMetadata.mediaAction === 'replace' && !fileMetadata.replaceMediaName));
      return baseInvalid || altTextInvalid || imageActionInvalid || mediaActionInvalid;
    });

    if (missingFields.length > 0) {
      setShowValidationError(true);
      toastError("A required field is missing. Please fill out all required fields and try again.");
      return; // Stop the upload if validation fails
    }
    setShowValidationError(false);
    // All validation passed. Trim and sanitize comments at submit time before upload.
    const sanitizeComment = (value) => {
      if (typeof value !== 'string') return '';
      // Remove HTML tags to prevent stored HTML injection
      return value.replace(/<[^>]*>/gm, '').trim();
    };
    
    setAttachmentComment(prev =>
      prev.map(item => ({ ...item, comment: sanitizeComment(item.comment) }))
    );
    // Ensure irrelevant fields (altTextGeneration/imageAction/replaceImageName/
    // mediaAction/replaceMediaName) are set to null for non-matching categories
    const updatedComments = selectedFilesWithComment.map((file) => {
      const existingComment = attachmentComment.find(item => item.file === file.name) || {};
      const isImage = existingComment.filetype === 'imageAttachment';
      const isMedia = existingComment.filetype === 'mediaAttachment';
      return {
        ...existingComment,
        file: file.name,
        comment: sanitizeComment(existingComment.comment || ''),
        altTextGeneration: isImage ? existingComment.altTextGeneration : null,
        imageAction: isImage ? existingComment.imageAction : null,
        replaceImageName: isImage && existingComment.imageAction === 'replace' ? existingComment.replaceImageName : null,
        mediaAction: isMedia ? existingComment.mediaAction : null,
        replaceMediaName: isMedia && existingComment.mediaAction === 'replace' ? existingComment.replaceMediaName : null
      };
    });

    proceedWithUpload(updatedComments);
  };

  const handleAltTextChange = (filename, value) => {
    setAttachmentComment(prev =>
      prev.map(item => (item.file === filename ? { ...item, altTextGeneration: value } : item))
    );
  };

  const proceedWithUpload = (updatedComments) => {
    setLoader(true);
    props.getTrigger(false);

    try {
      let GetDetailDataString = localStorage.getItem(`${userProfile.docid}_init-getdetails`);
      let GetDetailData = JSON.parse(GetDetailDataString);
      var formdata = new FormData();

      // Add base data
      formdata.append("jobGUID", GetDetailData.Articledetails.ArticleGUID);
      formdata.append("ActivityID", GetDetailData.ActivtyDetails.ActivityID);
      formdata.append("docid", userProfile.docid);
      formdata.append("type", "attachment");

      // Validate file sizes and append data
      const oversizedFiles = [];
      const maxFileSize = 40 * 1024 * 1024; // 40MB

      // Index the backend sees: only files actually appended are counted, so a
      // skipped oversized file must not leave a gap in the metadata arrays.
      let appendIndex = 0;

      selectedFilesWithComment.forEach((file) => {
        if (file.size > maxFileSize) {
          oversizedFiles.push(file.name);
          return;
        }

        const fileMetadata = updatedComments.find(item => item.file === file.name) || {};

        formdata.append("files", file);
        formdata.append(`fileTypes[${appendIndex}]`, fileMetadata.filetype);
        formdata.append(`fileComments[${appendIndex}]`, fileMetadata.comment);
        formdata.append(`altTextGeneration[${appendIndex}]`, alttextEnabled ? fileMetadata.altTextGeneration : null);
        formdata.append(`imageAction[${appendIndex}]`, fileMetadata.imageAction || null);
        formdata.append(`replaceImageName[${appendIndex}]`, fileMetadata.replaceImageName || null);
        formdata.append(`mediaAction[${appendIndex}]`, fileMetadata.mediaAction || null);
        formdata.append(`replaceMediaName[${appendIndex}]`, fileMetadata.replaceMediaName || null);
        appendIndex += 1;
      });

      if (oversizedFiles.length > 0) {
        toastError(`File size should be less than 40 MB: ${oversizedFiles.join(', ')}`);
        if (oversizedFiles.length === selectedFilesWithComment.length) {
          setLoader(false);
          setShowCommentModal(false);
          setSelectedFilesWithComment([]);
          setAttachmentComment([]);
          return;
        }
      }

      const url = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].upload_attachment}`;

      axios.post(url, formdata, {
        headers: { "Content-Type": "multipart/form-data" },
      })
        .then((response) => {
          setLoader(false);
          props.refreshAttachments(true);
          setShowCommentModal(false);
          setSelectedFilesWithComment([]);
          setAttachmentComment([]);
          // Reset file input to allow re-selection of the same file
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          if (response.data.SuccessFiles.length !== 0) {
            response.data.SuccessFiles.forEach(item =>
              toastSuccess(`${item.data[0].Filename} - ${item.message}`)
            );
          }
          if (response.data.FailedFiles.length !== 0) {
            response.data.FailedFiles.forEach(item =>
              toastError(`${item.data[0].Filename} - ${item.message}`)
            );
          }
        })
        .catch(error => {
          console.log(error);
          // Surface the backend's message (e.g. "This file format is restricted…")
          // which is returned as { error: "…" }; fall back to the generic message.
          toastError(error.response?.data?.error || error.message);
          setLoader(false);
        });

    } catch (error) {
      console.log(error);
      toastError(error.message);
      setLoader(false);
    }
  };



  // Resolve a MIME type from a filename, mirroring the Supplementary download flow.
  const getMimeTypeForFile = (filename) => {
    const ext = (filename.split('.').pop() || '').toLowerCase();
    switch (ext) {
      case 'doc': return 'application/msword';
      case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'xls': return 'application/vnd.ms-excel';
      case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'ppt': return 'application/vnd.ms-powerpoint';
      case 'pptx': return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      case '7z': return 'application/x-7z-compressed';
      case 'swf': return 'application/x-shockwave-flash';
      default: return `application/${ext || 'octet-stream'}`;
    }
  };

  const handleDownloadAll = async () => {
    if (zipping) return;
    if (!file || file.length === 0) {
      toastError('No attachments to download.');
      return;
    }
    setZipping(true);
    try {
      const zip = new JSZip();
      const usedNames = {};
      let addedCount = 0;

      // Fetch each attachment directly from its SAS URL — the same source the
      // single-file download uses (the download anchor's href={item.SASFilepath}).
      await Promise.all(
        file.map(async (item) => {
          try {
            const mimeType = getMimeTypeForFile(item.Filename || '');
            const response = await axios.get(item.SASFilepath, { responseType: 'blob' });

            // Ensure unique names inside the zip if duplicates exist
            let name = item.Filename || 'attachment';
            if (usedNames[name] !== undefined) {
              usedNames[name] += 1;
              const dot = name.lastIndexOf('.');
              name = dot > 0
                ? `${name.slice(0, dot)}(${usedNames[name]})${name.slice(dot)}`
                : `${name}(${usedNames[name]})`;
            } else {
              usedNames[item.Filename || 'attachment'] = 0;
            }
            zip.file(name, new Blob([response.data], { type: mimeType }));
            addedCount += 1;
          } catch (err) {
            console.log('Failed to fetch attachment for zip:', item.Filename, err);
            toastError(`Failed to download ${item.Filename}`);
          }
        })
      );

      if (addedCount === 0) {
        toastError('Unable to download attachments.');
        return;
      }

      // Name the zip after the article/document, e.g. NEUCOM130927_attachment.zip
      let GetDetailDataString = localStorage.getItem(`${userProfile.docid}_init-getdetails`);
      let GetDetailData = JSON.parse(GetDetailDataString);
      const articleName = GetDetailData?.Articledetails?.ArticleName || 'attachments';
      const zipName = `${articleName}_attachment.zip`;

      const content = await zip.generateAsync({ type: 'blob' });
      const blobUrl = window.URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = zipName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toastSuccess('Attachments downloaded successfully');
    } catch (error) {
      console.log(error);
      toastError(error.message);
    } finally {
      setZipping(false);
    }
  };

  const deleteConfirm = (value) => {
    setDeleteData(value)
    setConfirm(true)
  }

  const deleteAttchment = () => {
    props.getTrigger(false)
    let dataValue = deleteData;
    let GetDetailDataString = localStorage.getItem(`${userProfile.docid}_init-getdetails`);
    let GetDetailData = JSON.parse(GetDetailDataString);
    let data = {
      "ArticleGUID": dataValue.ArticleGUID,
      "AttachID": dataValue.AttachID,
      "Proof_Type": GetDetailData.jobdetails.ProofType,
      "AttachFlag": dataValue.AttachFlag,
      "docid": userProfile.docid
    }
    const deleteUrl = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].deleteAttachment}`
    axios.post(deleteUrl, data).then((response) => {
      setConfirm(false)
      props.getTrigger(true)
      props.refreshAttachments(); 
      toastSuccess(`${response.data.data[0].Filename} - ${response.data.message}`)
    }
    ).catch((error) => {
      console.log(error);
      toastError(error.message);
    })
  }

  function getFileIcon(filetype) {
    switch (filetype) {
      case "png": return Png
      case "pdf": return Pdf
      case "jpg":
      case "jpeg": return Jpg
      case "docx": return Docx
      case "doc": return Doc
      case "tiff": return Tiff
      case "tif": return Tif
      case "zip": return Zip
      case "txt": return Txt
      case "xlsx": return Xlsx
      case "json": return Json
      case "xml": return Xml
      case "ini": return Ini
      default: return Noimage
    }
  }

  const handleSelectType = (value, filename, index) => {
    if (value === 'mediaAttachment' && !isValidMediaFile(filename)) {
      toastError(`"${filename}" is not a valid Media file. Allowed formats: ${ALLOWED_MEDIA_EXTENSIONS.map(ext => '.' + ext).join(', ')}`);
      return;
    }
    if (value === 'imageAttachment' && !isValidImageFile(filename)) {
      toastError(`"${filename}" is not a valid Image file. Allowed formats: ${ALLOWED_IMAGE_EXTENSIONS.map(ext => '.' + ext).join(', ')}`);
      return;
    }
    const isImage = value === 'imageAttachment';
    const isMedia = value === 'mediaAttachment';
    const existing = attachmentComment.find(it => it.file === filename);
    let newComment = {
      file: filename,
      filetype: value,
      comment: existing?.comment || "",
      altTextGeneration: isImage ? (existing?.altTextGeneration || '') : null,
      imageAction: isImage ? (existing?.imageAction || '') : null,
      replaceImageName: isImage ? (existing?.replaceImageName || '') : null,
      mediaAction: isMedia ? (existing?.mediaAction || '') : null,
      replaceMediaName: isMedia ? (existing?.replaceMediaName || '') : null
    };

    // Check if the file is already present
    if (!attachmentComment.some(item => item.file === filename)) {
      setAttachmentComment([...attachmentComment, newComment]);
    } else {
      // Optional: Update the existing entry if needed
      setAttachmentComment(
        attachmentComment.map(item =>
          item.file === filename
            ? {
                ...item,
                filetype: value,
                altTextGeneration: isImage ? (item.altTextGeneration || '') : null,
                imageAction: isImage ? (item.imageAction || '') : null,
                replaceImageName: isImage ? (item.replaceImageName || '') : null,
                mediaAction: isMedia ? (item.mediaAction || '') : null,
                replaceMediaName: isMedia ? (item.replaceMediaName || '') : null
              }
            : item
        )
      );
    }
  };

  // Handler for the "New Image" / "Replace Image" choice
  const handleImageActionChange = (filename, value) => {
    const existing = attachmentComment.find(it => it.file === filename);
    const newEntry = {
      file: filename,
      filetype: existing?.filetype || 'imageAttachment',
      comment: existing?.comment || '',
      // "New Image" always needs fresh alt-text, so auto-select the
      // "completely new image" option (the other one is disabled in the UI).
      // "Replace Image" clears the choice so the user picks explicitly.
      altTextGeneration: value === 'new' ? 'yes' : '',
      imageAction: value,
      // Selecting "New Image" clears any previously chosen image to replace
      replaceImageName: value === 'replace' ? (existing?.replaceImageName || '') : ''
    };

    if (!attachmentComment.some(item => item.file === filename)) {
      setAttachmentComment([...attachmentComment, newEntry]);
    } else {
      setAttachmentComment(
        attachmentComment.map(item =>
          item.file === filename
            ? {
                ...item,
                imageAction: value,
                altTextGeneration: value === 'new' ? 'yes' : '',
                replaceImageName: value === 'replace' ? (item.replaceImageName || '') : ''
              }
            : item
        )
      );
    }
  };

  // Handler for picking the exact existing image being replaced
  const handleReplaceImageNameChange = (filename, value) => {
    setAttachmentComment(prev =>
      prev.map(item => (item.file === filename ? { ...item, replaceImageName: value } : item))
    );
  };

  // Handler for the "New File" / "Replace File" choice (media attachments)
  const handleMediaActionChange = (filename, value) => {
    const existing = attachmentComment.find(it => it.file === filename);
    const newEntry = {
      file: filename,
      filetype: existing?.filetype || 'mediaAttachment',
      comment: existing?.comment || '',
      mediaAction: value,
      // Selecting "New File" clears any previously chosen file to replace
      replaceMediaName: value === 'replace' ? (existing?.replaceMediaName || '') : ''
    };

    if (!attachmentComment.some(item => item.file === filename)) {
      setAttachmentComment([...attachmentComment, newEntry]);
    } else {
      setAttachmentComment(
        attachmentComment.map(item =>
          item.file === filename
            ? { ...item, mediaAction: value, replaceMediaName: value === 'replace' ? (item.replaceMediaName || '') : '' }
            : item
        )
      );
    }
  };

  // Handler for picking the exact existing supplementary file being replaced
  const handleReplaceMediaNameChange = (filename, value) => {
    setAttachmentComment(prev =>
      prev.map(item => (item.file === filename ? { ...item, replaceMediaName: value } : item))
    );
  };

  // Function to handle adding or updating a comment for a filename
  const handleAddComment = (comment, filename, index) => {
    const existing = attachmentComment.find(item => item.file === filename);
    const isImage = existing?.filetype === 'imageAttachment';
    const isMedia = existing?.filetype === 'mediaAttachment';
    let newComment = {
      file: filename,
      filetype: existing?.filetype || '',
      comment: comment || "",
      altTextGeneration: existing?.altTextGeneration !== undefined ? existing.altTextGeneration : (isImage ? '' : null),
      imageAction: existing?.imageAction !== undefined ? existing.imageAction : (isImage ? '' : null),
      replaceImageName: existing?.replaceImageName !== undefined ? existing.replaceImageName : (isImage ? '' : null),
      mediaAction: existing?.mediaAction !== undefined ? existing.mediaAction : (isMedia ? '' : null),
      replaceMediaName: existing?.replaceMediaName !== undefined ? existing.replaceMediaName : (isMedia ? '' : null)
    };

    // Check if the file is already present
    if (!attachmentComment.some(item => item.file === filename)) {
      setAttachmentComment([...attachmentComment, newComment]);
    } else {
      // Optional: Update the existing entry if needed
      setAttachmentComment(
        attachmentComment.map(item =>
          item.file === filename ? { ...item, comment: comment } : item
        )
      );
    }
  };

  const RoleSplit = (value) => {
    const roleArray = JSON.parse(value);
    const roleName = roleArray[0].RoleName;
    return roleName;
  }

  const getAttachmentTypeDisplay = (type) => {
    switch (type) {
      case 'imageAttachment':
        return 'Image Attachment'
      case 'textAttachment':
        return 'Text Attachment'
      case 'commentAttachment':
        return 'Comment Attachment'
      case 'mediaAttachment':
        return 'Media Attachment'
      case 'otherAttachment':
        return 'Other Attachment'
      default:
        return type
    }
  }

  return (
    <div className="attachement_area">
      <main className="slider-header">
        <h2>Attachement</h2>
        <div>
          {userProfile?.role === 'Master Copier' && (
            <button
              className="download-all-btn"
              title="Download all as zip"
              aria-label="Download all attachments as zip"
              tabIndex={0}
              disabled={zipping || file.length === 0}
              onClick={handleDownloadAll}
            >
              {zipping ? <span className="loader-small" aria-hidden="true"></span> : <FaDownload aria-hidden="true" />}
            </button>
          )}
          <button
            className="close-btn"
            title="Close"
            tabIndex={0}
            ref={closeBtnRef}
            onClick={() => {
              setSidebarClose(true);
              // After closing, focus the Attachement tab button
              setTimeout(() => {
                const attBtn = document.querySelector('button[title="Attachement Tab"]');
                if (attBtn) attBtn.focus();
              }, 0);
            }}
            onKeyDown={(e) => {
              // Support Enter/Space to activate close and then restore focus
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSidebarClose(true);
                setTimeout(() => {
                  const attBtn = document.querySelector('button[title="Attachement Tab"]');
                  if (attBtn) attBtn.focus();
                }, 0);
              }
            }}
          >
            <FaTimes />
          </button>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      <div className={`modal-outer ${showconfirm ? "active" : ""}`}>
        <div className="modal confirmation-popup">
          <div className="modal-header">
            <h1>Confirm Delete</h1>
            <button className="modal-close-btn" tabIndex={0} aria-label="Close" onClick={() => setConfirm(false)}><FaTimes /></button>
          </div>
          <div className="modal-body">
            <p style={{ textAlign: 'center', margin: '10px 0' }}>Are you sure you want to delete this file?</p>
          </div>
          <div className="modal-footer">
            <button
              className="primary"
              tabIndex={0}
              ref={yesBtnRef}
              onClick={deleteAttchment}
            >Yes</button>
            <button
              className="outline-secondary"
              tabIndex={0}
              onClick={() => setConfirm(false)}
            >No</button>
          </div>
        </div>
      </div>
      {/* Comment Modal - Alternative UI */}
      <div className={`modal-outer ${showCommentModal ? "active" : ""}`}>
        <div className="modal att-comment-modal">
          {/* Header */}
          <div className="modal-header">
            <h1>Add Comment for Attachment</h1>
            <button
              className="modal-close-btn"
              tabIndex={0}
              aria-label="Close"
              onClick={() => {
                setShowCommentModal(false);
                setSelectedFilesWithComment([]);
                setAttachmentComment([]);
                setShowValidationError(false);
                if (fileInputRef.current) {
                  fileInputRef.current.value = null;
                }
              }}
            >
              <FaTimes />
            </button>
          </div>

          {/* Body */}
          <div className="modal-body att-modal-body">
            {selectedFilesWithComment && (
              <>
                {Array.from(selectedFilesWithComment).map((file, index) => {
                  const meta = attachmentComment.find(it => it.file === file.name);
                  const isImage = meta?.filetype === 'imageAttachment';
                  const isMedia = meta?.filetype === 'mediaAttachment';
                  const categoryInvalid = showValidationError && !meta?.filetype;
                  const commentInvalid = showValidationError && !meta?.comment;
                  const imageActionInvalid = showValidationError && alttextEnabled && isImage && !meta?.imageAction;
                  const replaceImageInvalid = showValidationError && alttextEnabled && isImage && meta?.imageAction === 'replace' && !meta?.replaceImageName;
                  const altTextInvalid = showValidationError && alttextEnabled && isImage && !meta?.altTextGeneration;
                  const mediaActionInvalid = showValidationError && alttextEnabled && isMedia && !meta?.mediaAction;
                  const replaceMediaInvalid = showValidationError && alttextEnabled && isMedia && meta?.mediaAction === 'replace' && !meta?.replaceMediaName;
                  return (
                  <div className="att-file-card" key={index}>
                    <div className="att-file-card-header">
                      <div className="att-file-icon">
                        {file.name.split('.').pop().substring(0, 2).toUpperCase()}
                      </div>
                      <span title={file.name}>{file.name}</span>
                    </div>
                    <div className="att-file-card-body">
                      {/* Category */}
                      <div className="att-form-group">
                        <label className="att-form-label">
                          Category <span className="att-required">*</span>
                        </label>
                        <select
                          ref={index === 0 ? firstCategoryRef : null}
                          value={attachmentComment.find(it => it.file === file.name)?.filetype || ''}
                          onChange={(e) => handleSelectType(e.target.value, file.name, index)}
                          className={`att-select ${categoryInvalid ? 'att-invalid' : ''}`}
                          required
                          tabIndex={0}
                        >
                          <option value="">-- Choose a category --</option>
                          <option value="imageAttachment">Image</option>
                          <option value="textAttachment">Text</option>
                          <option value="mediaAttachment">Media</option>
                          <option value="otherAttachment">Other</option>
                        </select>
                      </div>

                      {/* New Image / Replace Image choice (shown for image attachments
                          only when alttextEnabled). Custom listbox (not a native
                          <select>) so the labels can be bold. */}
                      {alttextEnabled && attachmentComment.find(it => it.file === file.name)?.filetype === 'imageAttachment' && (() => {
                        const currentImageAction = attachmentComment.find(it => it.file === file.name)?.imageAction || '';
                        const imageActionOptions = [
                          { value: 'new', label: 'New Image', description: 'Adding a new figure entirely (never existed before)' },
                          { value: 'replace', label: 'Replace Image', description: "Swapping an existing figure's file (updates a current one)" },
                        ];
                        const currentOption = imageActionOptions.find(opt => opt.value === currentImageAction);
                        return (
                          <div className="att-form-group">
                            <label className="att-form-label">
                              Image Type <span className="att-required">*</span>
                            </label>
                            <div
                              className="att-custom-select"
                              ref={imageTypeDropdownOpenFile === file.name ? imageTypeDropdownRef : null}
                            >
                              <button
                                type="button"
                                className={`att-custom-select-trigger ${imageActionInvalid ? 'att-invalid' : ''}`}
                                onClick={() =>
                                  setImageTypeDropdownOpenFile(prev => (prev === file.name ? null : file.name))
                                }
                                tabIndex={0}
                              >
                                <span>
                                  {currentOption ? (
                                    <><strong>{currentOption.label}</strong> – {currentOption.description}</>
                                  ) : '-- Choose an option --'}
                                </span>
                                <FaChevronDown className="att-custom-select-arrow" />
                              </button>
                              {imageTypeDropdownOpenFile === file.name && (
                                <ul className="att-custom-select-list" role="listbox">
                                  {imageActionOptions.map((opt) => {
                                    const isSelected = currentImageAction === opt.value;
                                    return (
                                      <li
                                        key={opt.value}
                                        role="option"
                                        aria-selected={isSelected}
                                        tabIndex={0}
                                        className={`att-custom-select-option ${isSelected ? 'selected' : ''}`}
                                        onClick={() => {
                                          handleImageActionChange(file.name, opt.value);
                                          setImageTypeDropdownOpenFile(null);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleImageActionChange(file.name, opt.value);
                                            setImageTypeDropdownOpenFile(null);
                                          }
                                        }}
                                      >
                                        <strong>{opt.label}</strong> – {opt.description}
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Existing image name picker, only when alttextEnabled and
                          "Replace Image" is chosen. Uses a custom listbox (not a
                          native <select>) because a native dropdown's popup height
                          can't be constrained via CSS. */}
                      {alttextEnabled &&
                        attachmentComment.find(it => it.file === file.name)?.filetype === 'imageAttachment' &&
                        attachmentComment.find(it => it.file === file.name)?.imageAction === 'replace' && (
                        <div className="att-form-group">
                          <label className="att-form-label">
                            Select Image to Replace <span className="att-required">*</span>
                          </label>
                          <div
                            className="att-custom-select"
                            ref={replaceDropdownOpenFile === file.name ? replaceDropdownRef : null}
                          >
                            <button
                              type="button"
                              className={`att-custom-select-trigger ${replaceImageInvalid ? 'att-invalid' : ''}`}
                              onClick={() =>
                                setReplaceDropdownOpenFile(prev => (prev === file.name ? null : file.name))
                              }
                              tabIndex={0}
                            >
                              <span>
                                {getImageDisplayTitle(attachmentComment.find(it => it.file === file.name)?.replaceImageName) || '-- Select image --'}
                              </span>
                              <FaChevronDown className="att-custom-select-arrow" />
                            </button>
                            {replaceDropdownOpenFile === file.name && (
                              <ul className="att-custom-select-list" role="listbox">
                                {[...imageFileList]
                                  .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
                                  .map((img, imgIndex) => {
                                    const isSelected = attachmentComment.find(it => it.file === file.name)?.replaceImageName === img.name;
                                    return (
                                      <li
                                        key={imgIndex}
                                        role="option"
                                        aria-selected={isSelected}
                                        tabIndex={0}
                                        className={`att-custom-select-option ${isSelected ? 'selected' : ''}`}
                                        onClick={() => {
                                          handleReplaceImageNameChange(file.name, img.name);
                                          setReplaceDropdownOpenFile(null);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleReplaceImageNameChange(file.name, img.name);
                                            setReplaceDropdownOpenFile(null);
                                          }
                                        }}
                                      >
                                        {getImageDisplayTitle(img.name)}
                                      </li>
                                    );
                                  })}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}

                      {/* New File / Replace File choice (shown for media attachments
                          only when alttextEnabled) */}
                      {alttextEnabled && attachmentComment.find(it => it.file === file.name)?.filetype === 'mediaAttachment' && (
                        <div className="att-form-group">
                          <label className="att-form-label">
                            File Type <span className="att-required">*</span>
                          </label>
                          <select
                            value={attachmentComment.find(it => it.file === file.name)?.mediaAction || ''}
                            onChange={(e) => handleMediaActionChange(file.name, e.target.value)}
                            className={`att-select ${mediaActionInvalid ? 'att-invalid' : ''}`}
                            required
                            tabIndex={0}
                          >
                            <option value="">-- Choose an option --</option>
                            <option value="new">New File</option>
                            <option value="replace">Replace File</option>
                          </select>
                        </div>
                      )}

                      {/* Existing supplementary file name picker, only when alttextEnabled
                          and "Replace File" is chosen. Uses the same custom listbox as
                          "Replace Image" so the popup height stays capped. */}
                      {alttextEnabled &&
                        attachmentComment.find(it => it.file === file.name)?.filetype === 'mediaAttachment' &&
                        attachmentComment.find(it => it.file === file.name)?.mediaAction === 'replace' && (
                        <div className="att-form-group">
                          <label className="att-form-label">
                            Select File to Replace <span className="att-required">*</span>
                          </label>
                          <div
                            className="att-custom-select"
                            ref={replaceDropdownOpenFile === file.name ? replaceDropdownRef : null}
                          >
                            <button
                              type="button"
                              className={`att-custom-select-trigger ${replaceMediaInvalid ? 'att-invalid' : ''}`}
                              onClick={() =>
                                setReplaceDropdownOpenFile(prev => (prev === file.name ? null : file.name))
                              }
                              tabIndex={0}
                            >
                              <span>
                                {attachmentComment.find(it => it.file === file.name)?.replaceMediaName || '-- Select file --'}
                              </span>
                              <FaChevronDown className="att-custom-select-arrow" />
                            </button>
                            {replaceDropdownOpenFile === file.name && (
                              <ul className="att-custom-select-list" role="listbox">
                                {[...supplementaryFileList]
                                  .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
                                  .map((suppFile, suppIndex) => {
                                    const isSelected = attachmentComment.find(it => it.file === file.name)?.replaceMediaName === suppFile.name;
                                    return (
                                      <li
                                        key={suppIndex}
                                        role="option"
                                        aria-selected={isSelected}
                                        tabIndex={0}
                                        className={`att-custom-select-option ${isSelected ? 'selected' : ''}`}
                                        onClick={() => {
                                          handleReplaceMediaNameChange(file.name, suppFile.name);
                                          setReplaceDropdownOpenFile(null);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleReplaceMediaNameChange(file.name, suppFile.name);
                                            setReplaceDropdownOpenFile(null);
                                          }
                                        }}
                                      >
                                        {suppFile.name}
                                      </li>
                                    );
                                  })}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Alt text options for images (only when alttextEnabled) */}
                      {alttextEnabled && userProfile.role === "Author" && attachmentComment.find(it => it.file === file.name)?.filetype === 'imageAttachment' && (() => {
                        const altEntry = attachmentComment.find(it => it.file === file.name);
                        // "New Image" always needs fresh alt-text: option 1 is
                        // auto-selected (see handleImageActionChange) and
                        // option 2 is disabled.
                        const isNewImage = altEntry?.imageAction === 'new';
                        return (
                        <div className="att-form-group">
                          <div className={`att-alt-section ${altTextInvalid ? 'att-invalid' : ''}`}>
                            <div className="att-alt-header">
                              {/* <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8zm9-3a1 1 0 11-2 0 1 1 0 012 0zM6.75 7.75a.75.75 0 000 1.5h.5V11a.75.75 0 001.5 0V8.5a.75.75 0 00-.75-.75h-1.25z" fill="#333"/></svg> */}
                              Alternative text option <span className="att-required">*</span>
                            </div>
                            <div className="att-alt-body">
                              <label
                                className={`att-alt-option ${altEntry?.altTextGeneration === 'yes' ? 'att-selected' : ''}`}
                              >
                                <input
                                  type="radio"
                                  name={`alt-${index}`}
                                  value="yes"
                                  checked={altEntry?.altTextGeneration === 'yes'}
                                  onChange={() => handleAltTextChange(file.name, 'yes')}
                                  required
                                />
                                <div className="att-alt-option-text">
                                  <strong>Add/replace with a completely new image</strong>
                                  <span>A new alt-text needs to be generated.</span>
                                </div>
                              </label>

                              <label
                                className={`att-alt-option ${altEntry?.altTextGeneration === 'no' ? 'att-selected' : ''}`}
                                style={isNewImage ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                                aria-disabled={isNewImage}
                                title={isNewImage ? 'Not applicable for a new image — new alt-text is always generated.' : undefined}
                              >
                                <input
                                  type="radio"
                                  name={`alt-${index}`}
                                  value="no"
                                  checked={altEntry?.altTextGeneration === 'no'}
                                  disabled={isNewImage}
                                  onChange={() => handleAltTextChange(file.name, 'no')}
                                />
                                <div className="att-alt-option-text">
                                  <strong>Upload the same image in higher resolution</strong>
                                  <span>Retain the previously generated alt-text</span>
                                </div>
                              </label>

                              <div className="att-info-banner">
                                <svg className="att-info-icon" width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8zm9-3a1 1 0 11-2 0 1 1 0 012 0zM6.75 7.75a.75.75 0 000 1.5h.5V11a.75.75 0 001.5 0V8.5a.75.75 0 00-.75-.75h-1.25z" fill="#0c5aa8"/></svg>
                                <span>If only the resolution changed (low to high), new alt text may not be required.</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        );
                      })()}

                      {/* Comment — moved to the bottom of the card, after all
                          type-specific fields */}
                      <div className="att-form-group">
                        <label className="att-form-label">
                          Comment <span className="att-required">*</span>
                        </label>
                        <textarea
                          value={attachmentComment.find(it => it.file === file.name)?.comment || ''}
                          onChange={(e) => handleAddComment(e.target.value, file.name)}
                          onKeyDown={(e) => e.stopPropagation()}
                          placeholder="Enter comment for attachment"
                          required
                          className={`att-textarea ${commentInvalid ? 'att-invalid' : ''}`}
                          tabIndex={0}
                        />
                      </div>
                    </div>
                  </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer att-modal-footer">
            {showValidationError && (
              <p className="att-validation-banner" role="alert">
                Please fill all the required fields.
              </p>
            )}
            <div className="att-modal-footer-actions">
              <button
                className="primary"
                tabIndex={0}
                onClick={handleUploadWithComment}
              >
                Upload
              </button>
              <button
                className="outline-secondary"
                tabIndex={0}
                onClick={() => {
                  setShowCommentModal(false);
                  setSelectedFilesWithComment([]);
                  setAttachmentComment([]);
                  setShowValidationError(false);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = null;
                  }
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      
      <div className="upload-area">
        <button
          className="upload-btn-wrapper"
          aria-label="File Upload"
        onKeyDown={e => {
          if (e.key === 'Tab' && !e.shiftKey && file.length === 0) {
            e.preventDefault();
            const tocBtn = document.getElementById('toc-tab-btn');
            if (tocBtn) tocBtn.focus();
            return;
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (fileInputRef.current) fileInputRef.current.click();
            return;
          }
        }}
        >
          <label htmlFor="file-upload" style={{ display: 'contents' }}>
            <FaCloudUploadAlt aria-hidden="true" />
            <p>Choose file or drag here</p>
          </label>
          <input
            id="file-upload"
            type="file"
            onChange={handleFileSelect}
            multiple
            ref={fileInputRef}
          />
        </button>

        <section className="upload-list-area" aria-label="Uploaded files list">
          {loader ? (
            <div className="fileupload-loder">
              <span className="loader" aria-hidden="true"></span>
            </div>
          ) : null}
          {file.length !== 0 ? (
            file.map((item, index) => (
              <div className="upload-list-area-inner" key={index}>
                <div className="icons-details">
                  <div className="upload-icons">
                    <img src={getFileIcon(item.filetype)} alt={item.filetype} />
                  </div>
                  <div className="upload-details" style={{lineHeight: "20px"}}>
                    <p aria-label={`filename:${item.Filename}`} tabIndex={0} title={item.Filename}>{item.Filename}</p>
                    <label aria-label={`username:${item.username}`} tabIndex={0}>{item.username}</label>
                    <span>
                      Date: {moment(item.CreatedOn).format("DD MMM YY")}, Size: {item.Filesize}
                    </span>
                    <a
                      href={item.SASFilepath}
                      download={item.Filename}
                      className="close-btn download-btn"
                      title="Download"
                      style={{ right: "21px" }}
                      onClick={() => toastSuccess(`${item.Filename} downloaded successfully`)}
                      tabIndex={0}
                    >
                      <FaDownload aria-hidden="true"/>
                    </a>

                    {RoleSplit(item.Rolename) === userProfile?.role && (
                      <button
                        className="close-btn attach-del-btn"
                        title="delete"
                        tabIndex={0}
                        onClick={() => deleteConfirm(item)}
                      >
                        <FaTrash aria-hidden="true"/>
                      </button>
                    )}
                  </div>
                  {/* {item.comment && (
                  <div className="attachment-comment">
                    <strong>Comment:</strong> {item.comment}
                  </div>
                )} */}
                </div>
                <div className="attachment-comment">
                  <p aria-label={`attachment type:${item.AttachType}`} tabIndex={0}><label>Type: </label>{getAttachmentTypeDisplay(item.AttachType)}</p>
             {item.Remarks && item.Remarks.length !== 0 ? (
                  <p
                    aria-label={`attachment comment:${item.Remarks}`}
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Tab' && !e.shiftKey) {
                        e.preventDefault();
                        // Try to focus the TOC tab by id
                        const tocBtn = document.getElementById('toc-tab-btn');
                        if (tocBtn) tocBtn.focus();
                      }
                    }}
                  >
                    <label>Comment:</label>
                    <span dangerouslySetInnerHTML={{ __html: item.Remarks }} />
                  </p>
                ) : null}
                {alttextEnabled && item.altTextGeneration !== "null" ? (
                  <p aria-label={`alternative text generation:${item.altTextGeneration}`} tabIndex={0}>
                    <label>Alt Text Generation:</label>
                    <span>{item.altTextGeneration === 'yes' ? 'Yes' : 'No'}</span>
                  </p>
                ) : ''}
                {item.AttachType === 'imageAttachment' && item.imageAction && item.imageAction !== 'null' ? (
                  <p aria-label={`image type:${item.imageAction}`} tabIndex={0}>
                    <label>Image Type:</label>
                    <span>{item.imageAction === 'replace' ? 'Replace Image' : 'New Image'}</span>
                  </p>
                ) : ''}
                {item.AttachType === 'imageAttachment' && item.imageAction === 'replace' && item.replaceImageName && item.replaceImageName !== 'null' ? (
                  <p aria-label={`image replaced:${item.replaceImageName}`} tabIndex={0}>
                    <label>Replaced Image:</label>
                    <span>{item.replaceImageName}</span>
                  </p>
                ) : ''}
                {item.AttachType === 'mediaAttachment' && item.mediaAction && item.mediaAction !== 'null' ? (
                  <p aria-label={`file type:${item.mediaAction}`} tabIndex={0}>
                    <label>File Type:</label>
                    <span>{item.mediaAction === 'replace' ? 'Replace File' : 'New File'}</span>
                  </p>
                ) : ''}
                {item.AttachType === 'mediaAttachment' && item.mediaAction === 'replace' && item.replaceMediaName && item.replaceMediaName !== 'null' ? (
                  <p aria-label={`file replaced:${item.replaceMediaName}`} tabIndex={0}>
                    <label>Replaced File:</label>
                    <span>{item.replaceMediaName}</span>
                  </p>
                ) : ''}
                </div>
              </div>
            ))
          ) : (
            <NodataFound />
          )}

        </section>
      </div>
    </div>
  );
}

export default Attachment_Sidebar;

